import React, { useEffect, useState } from "react"
import Select from "react-select"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { timeAgoShort } from "@/util/timeago"
import Avatar from "@/components/Messenger/Avatar"
import Fuse from "fuse.js"
import useDebounce from "@/util/useDebounce"
import { castPerson, personName } from "@/components/Messenger/util"

function ConversationCard(props) {
  return (
    <div
      className={
        "conversation-card " +
        (props.isActive ? "conversation-card--is-active" : "")
      }
      onClick={() => {
        props.onClick(props)
      }}
      hidden={!props.isActive && props.isDeleted}
    >
      <div className="conversation-card__avatar">
        <Avatar unread={props.unread} {...props.person} />
      </div>
      <div className="conversation-card__title">
        {props.title}
        <div className="conversation-card__subtitle">
          {props.subtitle}
          {props.isDeleted && (
            <div className="conversation__title-right">
              <div className="danger">DELETED</div>
            </div>
          )}
        </div>
      </div>
      <div className="conversation-card__time-ago">
        {props.date && timeAgoShort(props.date)}
      </div>
    </div>
  )
}

function NewConversations(props) {
  const {
    search,
    searchUrl,
    startConversation,
    readOnly,
    conversations,
  } = props
  const [people, setPeople] = useState([])
  const debouncedSearch = useDebounce(search, 500)
  const existingPersonIds = new Set(
    conversations
      .filter(c => c.person.id && !c.discarded_at)
      .map(c => c.person.id)
  )

  useEffect(() => {
    if (search.length == 0 || readOnly) {
      setPeople([])
    } else {
      const location = window.location
      const url = new URL(
        `${location.protocol}//${location.host}${searchUrl}.json`
      )
      url.searchParams.append("query", debouncedSearch)
      url.searchParams.append("with_sms_enabled_phone_number", "true")
      fetch(url.toString())
        .then(body => body.json())
        .then(json =>
          json
            .filter(p => !existingPersonIds.has(p.id))
            .map(p => ({
              title: personName(p),
              person: castPerson(p),
            }))
        )
        .then(json => setPeople(json))
    }
  }, [debouncedSearch])

  if (people.length) {
    return (
      <div className="messenger__new-conversations">
        <div className="messenger__new-conversations-title">
          {props.translations.openConversationWith}
        </div>
        {people.map((p, i) => (
          <ConversationCard key={i} {...p} onClick={startConversation} />
        ))}
      </div>
    )
  } else {
    return null
  }
}

const ALL_CONVERSATIONS = "All Conversations"
const UNASSIGNED = "Unassigned"

export default class ConversationList extends React.Component {
  state = { search: "", activeChat: null, selectedUserId: ALL_CONVERSATIONS }

  navigateTo = chat => {
    if (this.state.activeChat != chat.id) {
      this.setState({ activeChat: chat.id })
      this.props.history.push(`/conversation/${chat.id}`)
    } else {
      console.log("Loading Conversation")
    }
  }

  componentDidMount() {
    if (this._currentUserHasASubscription()) {
      this.setState({ selectedUserId: this.props.currentUser.id })
    }
    const activeCard = this.refs.cards.getElementsByClassName(
      "conversation-card--is-active"
    )[0]
    if (activeCard) {
      console.log("[ConversationList] Scroll to show active card", activeCard)
      const amountOfPreviousCardToShow = 30
      this.refs.cards.scrollTop =
        activeCard.offsetTop - amountOfPreviousCardToShow
    }
  }

  updateSearch = event => {
    this.setState({ search: event.target.value })
  }

  updateSelectedUser = option => {
    this.setState({ selectedUserId: option.value })
  }

  filteredConversations() {
    let conversationsFilteredByTitle
    if (this.state.search) {
      const fuse = new Fuse(this.props.conversations, {
        keys: ["title"],
      })
      conversationsFilteredByTitle = fuse
        .search(this.state.search)
        .map(result => result.item)
    } else {
      conversationsFilteredByTitle = this.props.conversations
    }
    if (this.state.selectedUserId == ALL_CONVERSATIONS) {
      return conversationsFilteredByTitle
    } else if (this.state.selectedUserId == UNASSIGNED) {
      return conversationsFilteredByTitle.filter(
        c => c.subscriptions.length == 0
      )
    } else {
      return conversationsFilteredByTitle.filter(
        c =>
          c.subscriptions.filter(s => s.user_id == this.state.selectedUserId)
            .length > 0
      )
    }
  }

  userOptions() {
    const unassignedUser = {
      label: this.props.translations.unassigned,
      value: UNASSIGNED,
    }
    const userIds = new Set()
    const userOptions = new Set()
    userIds.add(this.props.currentUser.id)
    userOptions.add(this._buildUserOption(this.props.currentUser))
    this.props.conversations.forEach((conversation, _) => {
      conversation.subscriptions.forEach((subscription, _) => {
        const user = subscription.user
        const userOption = this._buildUserOption(user)
        if (!userIds.has(user.id)) {
          userIds.add(user.id)
          userOptions.add(userOption)
        }
      })
      if (conversation.subscriptions.length == 0) {
        userOptions.add(unassignedUser)
      }
    })
    const sortedUsers = Array.from(userOptions).sort((a, b) =>
      a.label.localeCompare(b.label)
    )
    return [
      {
        label: this.props.translations.allConversations,
        value: ALL_CONVERSATIONS,
      },
    ].concat(sortedUsers)
  }

  render() {
    const filteredConversations = this.filteredConversations()
    const conversationCards = filteredConversations.map(c => {
      const isActive = this.props.location.pathname.match(
        `/conversation/${c.id}(\/|$)`
      )
      const isDeleted = c.discarded_at
      return (
        <ConversationCard
          key={c.id}
          isActive={isActive}
          onClick={this.navigateTo}
          isDeleted={isDeleted}
          {...c}
        />
      )
    })
    return (
      <div className="messenger__conversations">
        <h3 className="messenger__conversations-title">
          {this.props.translations.conversations}
          <span className="messenger__conversations-user-filter">
            <Select
              onChange={this.updateSelectedUser}
              options={this.userOptions()}
              value={this.userOptions().find(
                user => user.value == this.state.selectedUserId
              )}
              menuPortalTarget={document.body}
              styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
            />
          </span>
        </h3>
        <div className="messenger__conversations-search">
          <FontAwesomeIcon
            className="messenger__conversations-search-icon"
            icon="search"
          />
          <input
            type="search"
            value={this.state.search}
            onChange={this.updateSearch}
            placeholder={this.props.translations.searchPlaceholder}
          />
        </div>
        <div className="messenger__conversations-cards" ref="cards">
          {conversationCards}
          <NewConversations
            searchUrl={this.props.searchUrl}
            search={this.state.search}
            startConversation={event => {
              this.setState({ search: "" })
              this.props.startConversation(event, this.props.history)
            }}
            readOnly={this.props.readOnly}
            conversations={this.props.conversations}
            translations={this.props.translations}
          />
          {!this.state.search &&
            filteredConversations.length >=
              this.props.conversationListLimit && (
              <div className="messenger__conversations-footer">
                {this.props.translations.endOfList}
                <br />
                {this.props.translations.endOfListHint}
              </div>
            )}
        </div>
      </div>
    )
  }

  _allSubscriptionUsers() {
    return this.props.conversations.flatMap(conversation =>
      conversation.subscriptions.map(subscription => subscription.user)
    )
  }

  _buildUserOption(user) {
    return {
      label: `${user.first_name} ${user.last_name}`,
      value: user.id,
    }
  }

  _currentUserHasASubscription() {
    return this._allSubscriptionUsers().find(
      user => user.id == this.props.currentUser.id
    )
  }
}
